import { useCallback, useMemo } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import ButtonBase from '@zenbusiness/zui/ButtonBase';

import { FORMATION_TASK_TYPES, getTimelineItems } from '#client/components/OrderTracker/utils';
import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';
import { useUrlResolver } from '#client/hooks/useUrlResolver';

import { useEnterpriseFulfillments } from './useEnterpriseFulfillments';

// Fulfillment statuses where the customer can act to move the service forward.
// Only `incomplete` (intake never started, no prior data to lose) links into the
// journey. `action_req` is intentionally excluded: re-entering the journey does not
// prefill previously submitted data, so it stays a non-clickable status pill (GROWTH-4930).
const ACTIONABLE_STATUSES = ['incomplete'];

const EIN_TASK_TYPE = 'ein_creation';

// EIN needs a completed formation. Completed tasks are dropped from the list, so any
// formation task still present (incomplete, in progress, or canceled) means there is no
// completed formation yet — keep EIN non-clickable.
const isEinBlockedByUnfinishedFormation = (item, allItems) =>
  item.type === EIN_TASK_TYPE && allItems.some((t) => FORMATION_TASK_TYPES.includes(t.type));

/**
 * Logic for the enterprise OrderTracker: reads orders-api fulfillments, builds the
 * timeline items (live status), and attaches the per-row link props for actionable
 * (link) rows. Display labels are resolved by the component.
 *
 * @returns {{
 *   loading: boolean,
 *   items: Array<object>
 * }}
 */
export function useEnterpriseOrderTracker() {
  const { items: fulfillments, loading } = useEnterpriseFulfillments();
  const resolve = useUrlResolver();
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);

  // The resolver's `journey` kind reproduces the jurisdiction-segment logic this hook
  // used to inline (insert `/:jurisdiction/` before the uuid when known). An unmapped
  // type or a missing businessEntityUuid both resolve to an empty url — non-navigable —
  // so projecting `.url` preserves the prior status-only behavior.
  const getServicePath = useCallback(
    (type, jurisdictionCode) => resolve(type, { context: 'enterprise', jurisdictionCode }).url,
    [resolve],
  );

  const getLinkProps = useCallback(
    (item, allItems) => {
      const href =
        ACTIONABLE_STATUSES.includes(item.status) && !isEinBlockedByUnfinishedFormation(item, allItems)
          ? getServicePath(item.type, item.jurisdictionCode)
          : '';

      // reserveChevronSpace keeps the status pill in a consistent right-side
      // column across actionable (chevron) and status-only rows (GROWTH-4667).
      if (href) {
        return {
          component: ButtonBase,
          clickstreamProps: { elementName: 'enterprise_order_tracker_item' },
          href,
          isClickable: true,
          reserveChevronSpace: true,
          showChevron: true,
        };
      }

      return {
        isClickable: false,
        reserveChevronSpace: true,
        showChevron: false,
      };
    },
    [getServicePath],
  );

  const timelineItems = useMemo(
    () => getTimelineItems(fulfillments, { keepLiveStatus: true, enableRaIr }),
    [fulfillments, enableRaIr],
  );

  const items = useMemo(
    () =>
      timelineItems.map((item) => ({
        ...item,
        linkProps: getLinkProps(item, timelineItems),
      })),
    [timelineItems, getLinkProps],
  );

  return {
    items,
    loading,
  };
}
